import { useEffect, useMemo, useRef, useState } from 'react'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import {
  portalStorage,
  useExchangeTokenMutation,
  useGetPortalMeQuery,
  useGetPortalOrdersQuery,
} from 'store/customer-portal/customer-portal-runtime.api'
import PortalDocumentsDialog from './portal-documents-dialog'
import { HighlightText } from './portal-search-highlight'
import { ACCENT_BLUE, ACCENT_BLUE_HOVER } from 'utils/brand-colors'
import { useDebouncedValue } from 'utils/use-debounced-value'

/** Aligns status badges and row action buttons (common 32px control pattern) */
const PORTAL_ROW_CONTROL_HEIGHT = 32
const PORTAL_PAGE_SIZE = 25
const PORTAL_SEARCH_DEBOUNCE_MS = 350

const matchesPortalSearch = (
  row: { orderNumber?: string; loadNumber?: string; waybillNumber?: string },
  query: string
) => {
  const q = query.toLowerCase()
  return [row.orderNumber, row.loadNumber, row.waybillNumber]
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(q))
}

const successGreen = '#2e7d32'

const statusChipSx = (status: string) => {
  const lower = status.toLowerCase()
  if (lower.includes('cancel')) {
    return {
      color: 'error.dark',
      bgcolor: alpha('#E52222', 0.1),
      border: '1px solid',
      borderColor: alpha('#E52222', 0.35),
    } as const
  }
  if (lower.includes('deliver')) {
    /* Read-only status: soft success chip — not full primary (avoids looking like the View action). */
    return {
      color: successGreen,
      bgcolor: alpha(successGreen, 0.12),
      border: '1px solid',
      borderColor: alpha(successGreen, 0.35),
    } as const
  }
  if (lower.includes('transit') || lower.includes('pickup') || lower.includes('progress')) {
    return {
      color: 'primary.dark',
      bgcolor: alpha(ACCENT_BLUE, 0.14),
      border: '1px solid',
      borderColor: alpha(ACCENT_BLUE, 0.35),
    } as const
  }
  if (lower.includes('new')) {
    return {
      color: 'primary.main',
      bgcolor: alpha(ACCENT_BLUE, 0.1),
      border: '1px solid',
      borderColor: alpha(ACCENT_BLUE, 0.28),
    } as const
  }
  return {
    color: 'text.secondary',
    bgcolor: 'grey.100',
    border: '1px solid',
    borderColor: 'divider',
  } as const
}

type PageState = 'bootstrapping' | 'ready' | 'expired'

const PortalPage = () => {
  const [state, setState] = useState<PageState>('bootstrapping')
  const [search, setSearch] = useState('')
  const [exchangeToken] = useExchangeTokenMutation()

  // Step 1 — bootstrap. Exchange the URL token (if present) for a session JWT.
  useEffect(() => {
    const run = async () => {
      const params = new URLSearchParams(window.location.search)
      const urlToken = params.get('t')

      if (urlToken) {
        try {
          const res = await exchangeToken({ token: urlToken }).unwrap()
          portalStorage.setAccessToken(res.accessToken)
          portalStorage.setLongToken(urlToken)
          // Scrub the token from the URL — keeps it out of history, referrer, logs.
          window.history.replaceState(null, '', '/portal')
          setState('ready')
          return
        } catch (e) {
          portalStorage.clear()
          setState('expired')
          return
        }
      }

      // No URL token — try a previously stored long token.
      const existingLong = portalStorage.getLongToken()
      if (existingLong) {
        try {
          const res = await exchangeToken({ token: existingLong }).unwrap()
          portalStorage.setAccessToken(res.accessToken)
          setState('ready')
        } catch (e) {
          portalStorage.clear()
          setState('expired')
        }
      } else {
        setState('expired')
      }
    }
    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  if (state === 'bootstrapping') {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '100vh' }}>
        <CircularProgress />
      </Stack>
    )
  }

  if (state === 'expired') {
    return (
      <Stack alignItems="center" justifyContent="center" sx={{ minHeight: '100vh', px: 3 }} spacing={1}>
        <Typography variant="h5">This link has expired.</Typography>
        <Typography color="text.secondary" textAlign="center">
          Please request a new link from your trucking company.
        </Typography>
      </Stack>
    )
  }

  return <PortalReady search={search} setSearch={setSearch} />
}

const PortalReady = ({
  search,
  setSearch,
}: {
  search: string
  setSearch: (v: string) => void
}) => {
  const { data: me } = useGetPortalMeQuery()
  const [pageIndex, setPageIndex] = useState(0)
  const debouncedSearch = useDebouncedValue(search, PORTAL_SEARCH_DEBOUNCE_MS)
  const searchQuery = debouncedSearch.trim()
  const isSearchPending = search.trim() !== searchQuery

  const { data: page, isLoading, isFetching } = useGetPortalOrdersQuery({
    page: pageIndex,
    size: PORTAL_PAGE_SIZE,
    search: searchQuery || undefined,
  })
  const [docsOrder, setDocsOrder] = useState<{ orderId: number; orderNumber?: string } | null>(null)
  const tableContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setPageIndex(0)
  }, [searchQuery])

  useEffect(() => {
    tableContainerRef.current?.scrollTo({ top: 0 })
  }, [pageIndex])

  const totalPages = page?.totalPages ?? 0
  const currentPage = page?.number ?? pageIndex
  const canGoPrev = currentPage > 0
  const canGoNext = currentPage + 1 < totalPages
  const showInitialLoading = isLoading && !page
  const apiRows = useMemo(() => page?.rows ?? [], [page?.rows])
  const rows = useMemo(() => {
    if (!searchQuery) return apiRows
    return apiRows.filter((r) => matchesPortalSearch(r, searchQuery))
  }, [apiRows, searchQuery])

  const isSearching = searchQuery.length > 0
  const serverFilteredThisPage =
    !searchQuery || apiRows.length === 0 || rows.length === apiRows.length
  const totalMatches = isSearching
    ? serverFilteredThisPage
      ? (page?.totalElements ?? rows.length)
      : rows.length
    : (page?.totalElements ?? 0)
  const showSearchSpinner = isSearchPending || (isFetching && isSearching)

  const exportCsv = () => {
    const headers = [
      'Order#',
      'Load#',
      'Waybill#',
      'Shipper',
      'Receiver',
      'Last Status',
      'Date/Time',
    ]
    const escape = (v: unknown) => {
      const s = v == null ? '' : String(v)
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"`
        : s
    }
    const lines = [headers.join(',')]
    for (const r of rows) {
      lines.push(
        [
          r.orderNumber,
          r.loadNumber,
          r.waybillNumber,
          r.shipperName,
          r.receiverName,
          r.lastStatus,
          r.lastStatusAt ?? '',
        ]
          .map(escape)
          .join(',')
      )
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'orders.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <Box
      sx={{
        height: '100dvh',
        bgcolor: '#eef1f6',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        component="header"
        sx={{
          flexShrink: 0,
          px: { xs: 2, sm: 3, md: 4 },
          py: 1.75,
          bgcolor: 'common.white',
          borderBottom: 1,
          borderColor: 'divider',
          boxShadow: '0 1px 0 rgba(15, 23, 42, 0.06)',
        }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={{ xs: 0.5, sm: 0 }}
        >
          <Typography sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 500 }}>
            {me?.customerEmail ?? 'Customer'}
          </Typography>
          <Typography sx={{ fontSize: 13, color: 'text.secondary', fontWeight: 600 }}>
            {me?.truckingCompanyName ?? 'Trucking Company'}
          </Typography>
        </Stack>
      </Box>

      <Box
        component="main"
        sx={{
          flex: 1,
          width: '100%',
          minWidth: 0,
          minHeight: 0,
          bgcolor: 'common.white',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            flex: 1,
            width: '100%',
            minHeight: 0,
            borderRadius: 0,
            border: 'none',
            boxShadow: 'none',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'common.white',
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              px: { xs: 2, sm: 3, md: 4 },
              pt: 2.5,
              pb: 2,
              borderBottom: 1,
              borderColor: 'divider',
              bgcolor: 'grey.50',
            }}
          >
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              justifyContent="space-between"
              alignItems={{ xs: 'stretch', md: 'center' }}
              spacing={2}
            >
              <Box>
                <Typography
                  sx={{
                    fontSize: { xs: 22, sm: 26 },
                    fontWeight: 700,
                    color: 'text.primary',
                    letterSpacing: '-0.02em',
                    lineHeight: 1.2,
                  }}
                >
                  Customer Portal
                </Typography>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.5 }}>
                  Track orders and download shipment documents.
                </Typography>
              </Box>
              <Box sx={{ minWidth: { xs: '100%', md: 320 } }}>
                <TextField
                  size="small"
                  fullWidth
                  placeholder="Search by Order#, Load#, or Waybill#…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  inputProps={{ 'aria-label': 'Search orders by order number, load number, or waybill number' }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary', fontSize: 22 }} />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        {showSearchSpinner ? (
                          <CircularProgress size={18} sx={{ color: 'text.secondary' }} />
                        ) : search ? (
                          <IconButton
                            size="small"
                            aria-label="Clear search"
                            onClick={() => setSearch('')}
                            edge="end"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        ) : null}
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                      bgcolor: 'common.white',
                      fontSize: 14,
                      '& fieldset': {
                        borderColor: alpha(ACCENT_BLUE, 0.12),
                      },
                      '&:hover fieldset': {
                        borderColor: alpha(ACCENT_BLUE, 0.35),
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: 'primary.main',
                        borderWidth: 1,
                      },
                    },
                  }}
                />
                {isSearching && !showInitialLoading && !isSearchPending && (
                  <Typography sx={{ fontSize: 12, color: 'text.secondary', mt: 0.75, px: 0.5 }}>
                    {totalMatches === 0
                      ? `No orders match “${searchQuery}”`
                      : totalMatches === 1
                        ? '1 order found'
                        : `${totalMatches} orders found`}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Box>

          <TableContainer
            ref={tableContainerRef}
            sx={{
              flex: 1,
              minHeight: 0,
              bgcolor: 'common.white',
              overflow: 'auto',
              px: { xs: 2, sm: 3, md: 4 },
              opacity: isFetching && !showInitialLoading ? 0.6 : 1,
              transition: 'opacity 0.15s ease',
            }}
          >
            <Table size="small" sx={{ minWidth: 920 }}>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: 'grey.50',
                    '& th': {
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    },
                  }}
                >
                  {[
                    'Order#',
                    'Load#',
                    'Waybill#',
                    'Shipper Name',
                    'Receiver Name',
                    'Last Status',
                    'Date/Time',
                  ].map((label) => (
                    <TableCell
                      key={label}
                      sx={{
                        fontSize: 11,
                        fontWeight: 700,
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        letterSpacing: '0.06em',
                        py: 1.75,
                      }}
                    >
                      {label}
                    </TableCell>
                  ))}
                  <TableCell
                    align="right"
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      py: 1.75,
                    }}
                  >
                    Documents
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {showInitialLoading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6, fontSize: 14, color: 'text.secondary' }}>
                      Loading…
                    </TableCell>
                  </TableRow>
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 6, fontSize: 14, color: 'text.secondary' }}>
                      {isSearching
                        ? `No orders match “${searchQuery}”. Try Order#, Load#, or Waybill#.`
                        : 'No orders to display.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r, index) => (
                    <TableRow
                      key={r.orderId}
                      hover
                      sx={{
                        bgcolor: index % 2 === 0 ? 'common.white' : alpha(ACCENT_BLUE, 0.03),
                        transition: 'background-color 0.15s ease, box-shadow 0.15s ease',
                        '&:hover': {
                          bgcolor: alpha(ACCENT_BLUE, 0.07),
                          boxShadow: `inset 0 0 0 1px ${alpha(ACCENT_BLUE, 0.35)}`,
                        },
                        '& td': {
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          fontSize: 14,
                          py: 1.5,
                          verticalAlign: 'middle',
                        },
                      }}
                    >
                      <TableCell sx={{ fontWeight: 600 }}>
                        <HighlightText text={r.orderNumber} query={searchQuery} />
                      </TableCell>
                      <TableCell>
                        <HighlightText text={r.loadNumber} query={searchQuery} />
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>
                        <HighlightText text={r.waybillNumber} query={searchQuery} />
                      </TableCell>
                      <TableCell>{r.shipperName ?? '—'}</TableCell>
                      <TableCell>{r.receiverName ?? '—'}</TableCell>
                      <TableCell sx={{ verticalAlign: 'middle' }}>
                        {r.lastStatus ? (
                          <Chip
                            label={r.lastStatus}
                            sx={{
                              height: PORTAL_ROW_CONTROL_HEIGHT,
                              minHeight: PORTAL_ROW_CONTROL_HEIGHT,
                              maxHeight: PORTAL_ROW_CONTROL_HEIGHT,
                              borderRadius: '4px',
                              fontWeight: 600,
                              cursor: 'default',
                              '& .MuiChip-label': {
                                px: 1.5,
                                py: 0,
                                fontSize: 13,
                                fontWeight: 600,
                                lineHeight: 1.2,
                              },
                              ...statusChipSx(r.lastStatus),
                            }}
                          />
                        ) : (
                          <Typography component="span" sx={{ color: 'text.secondary', fontSize: 14 }}>
                            —
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary', whiteSpace: 'nowrap' }}>
                        {r.lastStatusAt
                          ? new Date(r.lastStatusAt).toLocaleString(undefined, {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit',
                            })
                          : '—'}
                      </TableCell>
                      <TableCell align="right" sx={{ verticalAlign: 'middle' }}>
                        <Button
                          size="small"
                          variant="contained"
                          disableElevation
                          onClick={() =>
                            setDocsOrder({
                              orderId: r.orderId,
                              orderNumber: r.orderNumber,
                            })
                          }
                          sx={{
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: 13,
                            lineHeight: 1.2,
                            minWidth: 76,
                            minHeight: PORTAL_ROW_CONTROL_HEIGHT,
                            height: PORTAL_ROW_CONTROL_HEIGHT,
                            px: 1.5,
                            py: 0,
                            borderRadius: '4px',
                            bgcolor: ACCENT_BLUE,
                            '&:hover': { bgcolor: ACCENT_BLUE_HOVER },
                          }}
                        >
                          View
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
            spacing={1.5}
            sx={{
              flexShrink: 0,
              px: { xs: 2, sm: 3, md: 4 },
              py: 2,
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'grey.50',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <Box
                component="img"
                src="/images/_logo.png"
                alt="Transpots"
                sx={{ height: 28, width: 'auto', display: 'block' }}
              />
              <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                Powered by transpots.com
              </Typography>
            </Stack>

            {(totalPages > 1 || (isSearching && totalMatches > PORTAL_PAGE_SIZE)) && (
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <IconButton
                  size="small"
                  aria-label="Previous page"
                  disabled={!canGoPrev || isFetching}
                  onClick={() => setPageIndex((p) => Math.max(0, p - 1))}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: '4px',
                    bgcolor: 'common.white',
                  }}
                >
                  <ChevronLeftIcon fontSize="small" />
                </IconButton>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', px: 1, minWidth: 96, textAlign: 'center' }}>
                  Page {currentPage + 1} of {Math.max(totalPages, 1)}
                </Typography>
                <IconButton
                  size="small"
                  aria-label="Next page"
                  disabled={!canGoNext || isFetching}
                  onClick={() => setPageIndex((p) => p + 1)}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: '4px',
                    bgcolor: 'common.white',
                  }}
                >
                  <ChevronRightIcon fontSize="small" />
                </IconButton>
              </Stack>
            )}

            <Button
              variant="outlined"
              onClick={exportCsv}
              disabled={rows.length === 0}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: 13,
                borderRadius: '4px',
                px: 2.5,
                borderColor: alpha(ACCENT_BLUE, 0.45),
                color: ACCENT_BLUE,
                '&:hover': {
                  borderColor: ACCENT_BLUE,
                  bgcolor: alpha(ACCENT_BLUE, 0.06),
                },
              }}
            >
              Export CSV
            </Button>
          </Stack>
        </Paper>
      </Box>

      <PortalDocumentsDialog
        orderId={docsOrder?.orderId ?? null}
        orderNumber={docsOrder?.orderNumber}
        onClose={() => setDocsOrder(null)}
      />
    </Box>
  )
}

export default PortalPage
